import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import cs from 'classnames';
import { Channel, Chat, CustomMessageActions, MessageInput, MessageList, Thread, Window } from 'stream-chat-react';
import { EmojiPicker } from 'stream-chat-react/emojis';

import CustomMessage from 'features/messages/components/atoms/Message';
import NoChannelsPlaceholder from 'features/messages/components/atoms/NoChannelsPlaceholder';
import EditMessageInput from 'features/messages/components/EditMessageInput';
import ChannelList from 'features/messages/components/molecules/ChannelList';
import ChannelHeader from 'features/messages/components/molecules/MessageChannelHeader/MessageChannelHeader';
import { useMessaging } from 'features/messages/useMessaging';
import { CenteredSpinnerContainer } from 'features/ui/CenteredSpinner';
import Box from 'shared/components/display/Box.display';

import { MESSAGE_COPY } from 'shared/config/copy';
import { useCurrentUser } from 'shared/hooks/useCurrentUser';
import { useQuery } from 'shared/hooks/useQuery';
import Messaging from 'shared/messaging/Messaging';

import styles from './styles';

const CUSTOM_MESSAGE_ACTIONS = [
  'delete',
  'edit',
  'flag',
  // 'mute',
  'pin',
  'react',
  // 'reply',
];

interface Query {
  c: string;
}

type RouteParams = {
  organizationId?: string;
};

const MessageCenter = () => {
  const params = useParams<RouteParams>();
  const { activeChannel, setActiveChannel, setUnreadChannels } = useMessaging();
  const { currentUser, isCreator } = useCurrentUser();
  const { getQuery } = useQuery();

  const [loading, setLoading] = useState(true);
  const [keyboardOpen, setKeyboardOpen] = useState(false);

  const chatClient = Messaging.getClient();
  const query = getQuery<Query>();
  const organizationId = +(params.organizationId || 0);

  useEffect(() => {
    const setInitialActiveChannel = async (channelId) => {
      const channel = await Messaging.findAccountChannel(channelId);
      setActiveChannel(channel);
      setLoading(false);
    };

    setInitialActiveChannel(query.c);

    return () => {
      setActiveChannel(null);
    };
  }, []);

  const activeUnreadHandler = () => {
    setUnreadChannels();
  };

  const customMessageActions: CustomMessageActions = {
    'Mark as unread': (message) => {
      if (activeChannel) {
        activeChannel.markUnread({ message_id: message.id });
      }
    },
  };

  return loading ? (
    <CenteredSpinnerContainer />
  ) : (
    <Box css={styles}>
      <Chat
        client={chatClient}
        initialNavOpen={false}
        theme={cs('messaging', 'light', {
          'str-chat-no-scroll': keyboardOpen,
          'str-chat-for-brands': !isCreator,
        })}
      >
        <ChannelList
          activeChannel={activeChannel}
          organizationId={organizationId}
          currentUser={currentUser}
          chatClient={chatClient}
        />
        <Channel
          activeUnreadHandler={activeUnreadHandler}
          EmptyPlaceholder={<NoChannelsPlaceholder />}
          EmojiPicker={EmojiPicker}
          EditMessageInput={EditMessageInput}
        >
          <Window>
            <ChannelHeader />
            <MessageList
              Message={CustomMessage}
              messageActions={CUSTOM_MESSAGE_ACTIONS}
              customMessageActions={customMessageActions}
            />
            <MessageInput
              minRows={1}
              maxRows={10}
              additionalTextareaProps={{
                onFocus: () => setKeyboardOpen(true),
                onBlur: () => setKeyboardOpen(false),
                placeholder: activeChannel?.data?.frozen
                  ? MESSAGE_COPY.FROZEN_MESSAGE_INPUT_PLACEHOLDER
                  : MESSAGE_COPY.DEFAULT_MESSAGE_INPUT_PLACEHOLDER,
              }}
              hideSendButton={activeChannel?.data?.frozen}
            />
          </Window>
          <Thread messageActions={[]} Input={() => <div></div>} />
        </Channel>
      </Chat>
    </Box>
  );
};

export default MessageCenter;
